import {
  Box,
  ButtonGroup,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  styled,
} from '@mui/material';
import useGeoDataMutation from 'components/ComponentsLibrary/hooks/useGeoDataMutations';
import { useMessages } from 'components/hooks';
import { DialogDefault, ThemeButton, ThemeTypography } from 'designSystem';
import {
  EGeoDataExportFormat,
  EGeoDataExportType,
  TGeoDataExportFormat,
  TGeoDataExportType,
} from 'graphql/secure/mutations/types/geodata-mutation.types';
import React, { FC, useEffect, useState } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';

interface IDownloadGeoDataDialogProps extends IDefaultDialogProps {
  geoDataId: string;
  /**
   * If true, the dialog only allows downloading the validation file.
   */
  validationOnly?: boolean;
  disableGeojson?: boolean;
}

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(2),
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
  borderRadius: '6px',
  background: theme.custom.themeColors.primary[5],
  marginBottom: theme.spacing(2),
}));

const FormatSelection: FC<{
  format: TGeoDataExportFormat;
  options?: EGeoDataExportFormat[];
  onChange: (format: TGeoDataExportFormat) => void;
}> = ({
  format,
  options = [EGeoDataExportFormat.GEOJSON, EGeoDataExportFormat.CSV, EGeoDataExportFormat.EXCEL],
  onChange,
}) => (
  <Box display="flex" mt={1} flexDirection="column" gap={1}>
    <ThemeTypography variant="BODY_MEDIUM_BOLD">
      Select which file format to download.
    </ThemeTypography>
    <ButtonGroup>
      {options.includes(EGeoDataExportFormat.GEOJSON) && (
        <ThemeButton
          color="WHITE"
          className={format === EGeoDataExportFormat.GEOJSON ? 'selected' : ''}
          onClick={() => onChange(EGeoDataExportFormat.GEOJSON)}
        >
          .geojson
        </ThemeButton>
      )}
      {options.includes(EGeoDataExportFormat.EXCEL) && (
        <ThemeButton
          color="WHITE"
          className={format === EGeoDataExportFormat.EXCEL ? 'selected' : ''}
          onClick={() => onChange(EGeoDataExportFormat.EXCEL)}
        >
          .xlsx
        </ThemeButton>
      )}
      {options.includes(EGeoDataExportFormat.CSV) && (
        <ThemeButton
          color="WHITE"
          className={format === EGeoDataExportFormat.CSV ? 'selected' : ''}
          onClick={() => onChange(EGeoDataExportFormat.CSV)}
        >
          .csv
        </ThemeButton>
      )}
    </ButtonGroup>
    <ThemeTypography variant="BODY_MEDIUM">
      For geojson, all data points and any potential issues are included as properties for each
      plot, but might not always be accessible when opened, depending on the file manager.
    </ThemeTypography>
  </Box>
);

const DownloadGeoDataDialog: FC<IDownloadGeoDataDialogProps> = ({
  open,
  geoDataId,
  validationOnly,
  disableGeojson,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState<TGeoDataExportType>(
    validationOnly ? EGeoDataExportType.VALIDATED : EGeoDataExportType.VERIFIED
  );
  const [exportFormat, setExportFormat] = useState<TGeoDataExportFormat>(
    disableGeojson ? EGeoDataExportFormat.EXCEL : EGeoDataExportFormat.GEOJSON
  );

  const { setErrorMessage } = useMessages();
  const { exportGeoData } = useGeoDataMutation();

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await exportGeoData({
        geoDataId,
        exportType,
        exportFormat,
      });
      const fileUrl = response?.data?.exportGeoData?.fileUrl;
      if (fileUrl) {
        if (exportFormat === EGeoDataExportFormat.GEOJSON) {
          try {
            const response = await fetch(fileUrl);
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const urlParts = fileUrl.split('/');
            const filename = urlParts[urlParts.length - 1] || 'geo-data.geojson';
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (e) {
            throw new Error('Could not fetch the file for download');
          }
        } else {
          const link = document.createElement('a');
          link.href = fileUrl;
          link.target = '_blank';
          // Wait for the file to actually be ready to be downloaded
          await setTimeout(() => link.click(), 500);
        }
      } else {
        throw new Error('Could not export the dataset');
      }
    } catch (error) {
      setErrorMessage('Could not export the dataset. Please try again or contact the support');
    } finally {
      setLoading(false);
      onClose?.();
    }
  };

  useEffect(() => {
    if (
      exportType === EGeoDataExportType.VERIFIED &&
      exportFormat !== EGeoDataExportFormat.GEOJSON
    ) {
      setExportFormat(EGeoDataExportFormat.GEOJSON); // For verified datasets we only support GEOJSON format
    }
  }, [exportType, exportFormat]);

  return (
    <DialogDefault open={open} iconName="download" title="Download geo-data" onClose={onClose}>
      <DialogContent>
        <Container>
          <ThemeTypography variant="BODY_MEDIUM_BOLD">
            Select a version of the geo-dataset to download
          </ThemeTypography>
          <RadioGroup
            value={exportType}
            onChange={e => setExportType(e.target.value as TGeoDataExportType)}
          >
            <FormControlLabel
              value="validated"
              control={<Radio value={EGeoDataExportType.VALIDATED} size="small" />}
              label={
                <>
                  <ThemeTypography variant="BODY_MEDIUM_BOLD">Validated dataset</ThemeTypography>
                  <ThemeTypography variant="BODY_MEDIUM">
                    Initially imported geo-dataset including potential highlighted data formatting
                    errors.
                  </ThemeTypography>

                  {exportType === EGeoDataExportType.VALIDATED && (
                    <FormatSelection
                      format={exportFormat}
                      options={
                        disableGeojson
                          ? [EGeoDataExportFormat.EXCEL, EGeoDataExportFormat.CSV]
                          : undefined
                      }
                      onChange={setExportFormat}
                    />
                  )}
                </>
              }
            />

            {!validationOnly && (
              <>
                <Box my={2}>
                  <Divider />
                </Box>
                <FormControlLabel
                  value="verified"
                  control={<Radio value={EGeoDataExportType.VERIFIED} size="small" />}
                  label={
                    <>
                      <ThemeTypography variant="BODY_MEDIUM_BOLD">Verified dataset</ThemeTypography>
                      <ThemeTypography variant="BODY_MEDIUM">
                        Most recent status of the geo-data, including all assigned IDs, included
                        data points and any potential verification warnings about this dataset’s
                        EUDR-compatibility and readiness for deforestation risk analysis.
                      </ThemeTypography>

                      {exportType === EGeoDataExportType.VERIFIED && !disableGeojson && (
                        <FormatSelection
                          format={exportFormat}
                          options={[EGeoDataExportFormat.GEOJSON]}
                          onChange={setExportFormat}
                        />
                      )}
                    </>
                  }
                />
              </>
            )}
          </RadioGroup>
        </Container>

        <ThemeTypography variant="BODY_MEDIUM">
          You can share this geo-data with external partners and address any potential issues or
          missing details.
        </ThemeTypography>
      </DialogContent>

      <DialogActions>
        <Box display="flex" justifyContent="space-between" width="100%">
          <ThemeButton color="BLUE_ICE" onClick={onClose}>
            Cancel
          </ThemeButton>
          <ThemeButton loading={loading} onClick={handleExport}>
            Download
          </ThemeButton>
        </Box>
      </DialogActions>
    </DialogDefault>
  );
};

export default DownloadGeoDataDialog;
